import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getHostTheme, onHostTheme } from '@/services/embed/embedTheme';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

// A partitioned or blocked iframe store throws on access, which would take the mount down with it.
const readStoredTheme = (): Theme | null => {
  try {
    const saved = localStorage.getItem('theme');
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    return null;
  }
};

const storeTheme = (theme: Theme) => {
  try {
    localStorage.setItem('theme', theme);
  } catch {
    /* empty */
  }
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Host first: embedded, this provider mounts after the bridge has taken `init`, and the
    // stored value is whatever the standalone app last used — not what the host is showing.
    const host = getHostTheme();
    const saved = readStoredTheme();
    const initial =
      host ??
      saved ??
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    if (!saved) storeTheme(initial);
    document.documentElement.className = `${initial}-mode`;
    return initial;
  });

  useEffect(() => onHostTheme(setThemeState), []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    storeTheme(newTheme);
    document.documentElement.className = `${newTheme}-mode`;
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
